import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import ReactSelect from "react-select";
import toast from "react-hot-toast";
import moment from "moment";
import {
  HiOutlineCube,
  HiOutlineIdentification,
  HiOutlineChatAlt2,
  HiOutlineCheck,
  HiOutlineExclamationCircle,
  HiOutlineClipboardList,
  HiOutlineTag,
} from "react-icons/hi";
import { DefectService } from "../../../../services/DefectService";
import { StockService } from "../../../../services/StockService";
import { SpeciesBadge } from "./Defects";
import {
  DEFECTS_PATH,
  BackLink,
  Field,
  inputClass,
  selectStyles,
  primaryButtonClass,
} from "../shared/ui";

const REMARK_LIMIT = 500;

const emptyForm = { speciesId: null, stockNumber: "", contractorNumber: "", remark: "" };

const validate = (form) => {
  const errors = {};
  if (!form.speciesId) errors.speciesId = "Choose the species of the defective stock.";
  if (!form.stockNumber.trim()) errors.stockNumber = "Enter the stock number.";
  if (!form.contractorNumber.trim()) errors.contractorNumber = "Enter the contractor number.";
  if (!form.remark.trim()) errors.remark = "Describe the defect.";
  else if (form.remark.length > REMARK_LIMIT) errors.remark = `Keep the remark under ${REMARK_LIMIT} characters.`;
  return errors;
};

const DefectForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);

  const [form, setForm] = useState(emptyForm);
  const [species, setSpecies] = useState([]);
  const [speciesLoading, setSpeciesLoading] = useState(true);
  const [loadState, setLoadState] = useState(isEdit ? "loading" : "ready");
  const [dateCreated, setDateCreated] = useState(null);
  const [touched, setTouched] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    StockService.GetAllSpecies().then((res) => {
      setSpecies(
        (res || [])
          .map((s) => ({ value: s.speciesId, code: s.speciesCode, name: s.speciesName }))
          .sort((a, b) => (a.code || a.name || "").localeCompare(b.code || b.name || ""))
      );
      setSpeciesLoading(false);
    });
  }, []);

  useEffect(() => {
    if (!isEdit) return;
    DefectService.GetDefect(parseInt(id)).then((res) => {
      if (!res) {
        setLoadState("error");
        return;
      }
      setForm({
        speciesId: res.speciesId,
        stockNumber: res.stockNumber ?? "",
        contractorNumber: res.contractorNumber ?? "",
        remark: res.remark ?? "",
      });
      setDateCreated(res.dateCreated);
      setLoadState("ready");
    });
  }, [id, isEdit]);

  const errors = useMemo(() => validate(form), [form]);
  const showError = (field) => (submitted || touched[field]) && errors[field];
  const selectedSpecies = species.find((s) => s.value === form.speciesId) || null;

  const update = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));
  const touch = (field) => () => setTouched((t) => ({ ...t, [field]: true }));

  const handleSubmit = (e) => {
    e.preventDefault();
    setSubmitted(true);
    if (Object.keys(errors).length > 0) return;

    const payload = {
      speciesId: form.speciesId,
      stockNumber: form.stockNumber.trim(),
      contractorNumber: form.contractorNumber.trim(),
      remark: form.remark.trim(),
    };

    setSaving(true);
    const toastId = toast.loading(isEdit ? "Saving changes…" : "Logging defect…");
    const request = isEdit
      ? DefectService.UpdateDefect(parseInt(id), payload)
      : DefectService.AddDefect(payload);

    request.then((res) => {
      toast.dismiss(toastId);
      setSaving(false);
      if (res.Successful) {
        toast.success(res.message);
        navigate(DEFECTS_PATH);
      } else {
        toast.error(res.message);
      }
    });
  };

  const backLink = <BackLink to={DEFECTS_PATH}>All defects</BackLink>;

  if (loadState !== "ready") {
    return (
      <div className="w-full pb-10 space-y-6">
        {backLink}
        <div className="p-8 bg-white border shadow-sm rounded-2xl border-slate-200/80">
          {loadState === "loading" ? (
            <div className="space-y-5 animate-pulse">
              <div className="w-48 h-6 rounded bg-slate-200" />
              <div className="grid gap-5 md:grid-cols-2">
                <div className="h-12 rounded-xl bg-slate-100" />
                <div className="h-12 rounded-xl bg-slate-100" />
                <div className="h-12 rounded-xl bg-slate-100" />
              </div>
              <div className="h-28 rounded-xl bg-slate-100" />
            </div>
          ) : (
            <div className="flex flex-col items-center py-8 text-center">
              <span className="grid mb-4 rounded-full h-14 w-14 place-items-center bg-rose-50 text-rose-500">
                <HiOutlineExclamationCircle className="h-7 w-7" />
              </span>
              <h3 className="text-base font-semibold text-slate-800">Defect not found</h3>
              <p className="max-w-sm mt-1 text-sm text-slate-500">
                This defect could not be loaded. It may have been removed, or the connection failed.
              </p>
            </div>
          )}
        </div>
      </div>
    );
  }

  const remarkLength = form.remark.length;

  return (
    <div className="w-full pb-10 space-y-6">
      {backLink}

      <header className="flex items-start gap-4">
        <span className="grid w-12 h-12 shadow-sm shrink-0 place-items-center rounded-2xl bg-amber-50 text-amber-600 ring-1 ring-amber-100">
          <HiOutlineClipboardList className="w-6 h-6" />
        </span>
        <div>
          <h2 className="text-2xl font-bold tracking-tight text-slate-800">
            {isEdit ? `Defect #${id}` : "Log a defect"}
          </h2>
          <p className="mt-1 text-sm text-slate-500">
            {isEdit && dateCreated
              ? `Logged ${moment(dateCreated).format("DD MMM YYYY [at] HH:mm")}`
              : "Capture what was found and which stock and contractor it belongs to."}
          </p>
        </div>
      </header>

      <form onSubmit={handleSubmit} noValidate className="grid gap-6 lg:grid-cols-3">
        <div className="bg-white border shadow-sm lg:col-span-2 rounded-2xl border-slate-200/80">
          <section className="p-6 space-y-5">
            <div>
              <h3 className="text-sm font-semibold text-slate-800">Stock details</h3>
              <p className="text-xs text-slate-500">Identify the stock the defect was found on.</p>
            </div>

            <Field id="speciesId" label="Species" icon={HiOutlineTag} error={showError("speciesId")}>
              <ReactSelect
                inputId="speciesId"
                options={species}
                value={selectedSpecies}
                isLoading={speciesLoading}
                isSearchable
                placeholder="Search species by code or name"
                noOptionsMessage={() => (speciesLoading ? "Loading species…" : "No species found")}
                getOptionLabel={(o) => [o.code, o.name].filter(Boolean).join(" ")}
                formatOptionLabel={(o) => <SpeciesBadge code={o.code} name={o.name} />}
                onChange={(opt) => setForm((f) => ({ ...f, speciesId: opt ? opt.value : null }))}
                onBlur={touch("speciesId")}
                styles={selectStyles(Boolean(showError("speciesId")))}
                aria-invalid={Boolean(showError("speciesId"))}
              />
            </Field>

            <div className="grid gap-5 md:grid-cols-2">
              <Field
                id="stockNumber"
                label="Stock No"
                icon={HiOutlineCube}
                hint="As marked on the stock."
                error={showError("stockNumber")}
              >
                <input
                  id="stockNumber"
                  type="text"
                  autoComplete="off"
                  value={form.stockNumber}
                  onChange={update("stockNumber")}
                  onBlur={touch("stockNumber")}
                  placeholder="e.g. STK-00482"
                  aria-invalid={Boolean(showError("stockNumber"))}
                  className={`${inputClass(showError("stockNumber"))} font-mono`}
                />
              </Field>

              <Field
                id="contractorNumber"
                label="Contractor No"
                icon={HiOutlineIdentification}
                hint="The contractor responsible for this stock."
                error={showError("contractorNumber")}
              >
                <input
                  id="contractorNumber"
                  type="text"
                  autoComplete="off"
                  value={form.contractorNumber}
                  onChange={update("contractorNumber")}
                  onBlur={touch("contractorNumber")}
                  placeholder="e.g. CTR-117"
                  aria-invalid={Boolean(showError("contractorNumber"))}
                  className={`${inputClass(showError("contractorNumber"))} font-mono`}
                />
              </Field>
            </div>
          </section>

          <section className="p-6 border-t border-slate-100">
            <Field
              id="remark"
              label="Remark"
              icon={HiOutlineChatAlt2}
              hint="Describe the defect — what it is, where it is, how severe."
              error={showError("remark")}
              aside={
                <span
                  className={`shrink-0 tabular-nums ${
                    remarkLength > REMARK_LIMIT ? "text-rose-600" : "text-slate-400"
                  }`}
                >
                  {remarkLength}/{REMARK_LIMIT}
                </span>
              }
            >
              <textarea
                id="remark"
                rows={5}
                value={form.remark}
                onChange={update("remark")}
                onBlur={touch("remark")}
                placeholder="e.g. Heart rot visible at butt end, approx. 20 cm deep."
                aria-invalid={Boolean(showError("remark"))}
                className={`${inputClass(showError("remark"))} resize-y leading-relaxed`}
              />
            </Field>
          </section>

          <footer className="flex flex-col-reverse gap-3 px-6 py-4 border-t sm:flex-row sm:justify-end border-slate-100 bg-slate-50/60 rounded-b-2xl">
            <button
              type="button"
              onClick={() => navigate(DEFECTS_PATH)}
              className="rounded-xl px-4 py-2.5 text-sm font-semibold text-slate-600 transition hover:bg-slate-100"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className={primaryButtonClass}
            >
              {saving ? (
                <span className="w-4 h-4 border-2 rounded-full animate-spin border-white/40 border-t-white" />
              ) : (
                <HiOutlineCheck className="w-4 h-4" />
              )}
              {isEdit ? "Save changes" : "Log defect"}
            </button>
          </footer>
        </div>

        <aside className="hidden lg:block">
          <div className="sticky p-5 bg-white border shadow-sm top-6 rounded-2xl border-slate-200/80">
            <p className="text-xs font-semibold tracking-widest uppercase text-slate-400">Summary</p>
            <dl className="mt-4 space-y-4 text-sm">
              <div>
                <dt className="text-xs text-slate-400">Species</dt>
                <dd className="mt-1">
                  {selectedSpecies ? (
                    <SpeciesBadge code={selectedSpecies.code} name={selectedSpecies.name} />
                  ) : (
                    <span className="text-slate-300">Not selected</span>
                  )}
                </dd>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <dt className="text-xs text-slate-400">Stock no</dt>
                  <dd className="mt-1 font-mono break-all text-slate-800">
                    {form.stockNumber.trim() || <span className="font-sans text-slate-300">—</span>}
                  </dd>
                </div>
                <div>
                  <dt className="text-xs text-slate-400">Contractor no</dt>
                  <dd className="mt-1 font-mono break-all text-slate-800">
                    {form.contractorNumber.trim() || <span className="font-sans text-slate-300">—</span>}
                  </dd>
                </div>
              </div>
              <div>
                <dt className="text-xs text-slate-400">Remark</dt>
                <dd className="mt-1 whitespace-pre-line break-words text-slate-600">
                  {form.remark.trim() || <span className="text-slate-300">—</span>}
                </dd>
              </div>
            </dl>
          </div>
        </aside>
      </form>
    </div>
  );
};

export default DefectForm;
